import { useQuery } from "@tanstack/react-query";
import { OWGameResponse } from "@devour/client";
import getConfig from "../../utils/getConfig";

interface SearchEnhancedGamesResponse {
    data: OWGameResponse[];
    paginationInfo: {
        total: number;
        limit: number;
        offset: number;
    };
}

async function fetchAllGames(): Promise<OWGameResponse[]> {
    /*
     * Use the public enhanced-games/search endpoint
     * Get ALL games for comprehensive search functionality
     * This is cached for 24 hours via React Query, so it's efficient
     */
    const params = new URLSearchParams({
        limit: "3500", // Get all games (currently ~3200)
        offset: "0",
        // Removed seoEnabled filter to show ALL games
    });

    const response = await fetch(`${getConfig().basePath}/overwolf/enhanced-games/search?${params}`);

    if (!response.ok) {
        throw new Error(`Failed to fetch games: ${response.statusText}`);
    }

    const data: SearchEnhancedGamesResponse = await response.json();
    const games = data?.data || [];
    // Return all games with slugs - the modal can filter as needed
    return games.filter((game: OWGameResponse) => game && game.slug);
}

export const useGetAllGames = () => {
    const query = useQuery<OWGameResponse[]>({
        queryKey: ["overwolf-all-games"],
        queryFn: fetchAllGames,
        staleTime: 24 * 60 * 60 * 1000, // 24 hours - games rarely change
        gcTime: 24 * 60 * 60 * 1000, // Keep in cache for 24 hours (formerly cacheTime)
    });

    return {
        games: query.data || null,
        isLoading: query.isLoading,
        error: query.error ? "Failed to load games" : null,
    };
};